import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { KnowledgeBase } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Label } from "../components/ui/label.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { Skeleton } from "../components/ui/skeleton.jsx";
import { ArrowLeft, Save, Trash2, BookOpen, Eye } from 'lucide-react';
import ModernEditor from '../components/ui/modern-editor.jsx';

export default function EditKnowledge() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [article, setArticle] = useState({
    title: '',
    content: '',
    category: '',
    tags: []
  });
  const [newTag, setNewTag] = useState('');

  // Categorias disponíveis
  const baseCategorias = [
    'Geral',
    'Sistemas',
    'Suporte Técnico',
    'Faturamento',
    'Hardware',
    'Software',
    'Redes',
    'Procedimentos',
    'FAQ'
  ];

  // Incluir a categoria atual do artigo se não estiver na lista
  const categories = React.useMemo(() => {
    if (article.category && !baseCategorias.includes(article.category)) {
      return [article.category, ...baseCategorias];
    }
    return baseCategorias;
  }, [article.category]);

  useEffect(() => {
    loadArticle();
  }, []);

  const loadArticle = async () => {
    setIsLoading(true);
    try {
      // Obter ID do artigo da URL
      const urlParams = new URLSearchParams(window.location.search);
      const articleId = urlParams.get('id');
      
      if (!articleId) {
        navigate(createPageUrl('Knowledge'));
        return;
      }
      
      // Carregar dados do artigo
      const articleData = await KnowledgeBase.findById(articleId);
      
      if (!articleData) {
        navigate(createPageUrl('Knowledge'));
        return;
      }
      
      console.log('📄 Artigo carregado:', articleData);
      console.log('🏷️ Categoria do artigo:', articleData.category);
      
      setArticle(articleData);
    } catch (error) {
      console.error('Error loading knowledge article:', error);
      navigate(createPageUrl('Knowledge'));
    }
    setIsLoading(false);
  };

  const handleInputChange = (field, value) => {
    setArticle(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !article.tags.includes(newTag.trim())) {
      setArticle(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setArticle(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      console.log('🔄 Atualizando artigo com ID:', article.id);
      console.log('📝 Dados do artigo:', article);
      
      const result = await KnowledgeBase.update(article.id, {
        title: article.title,
        content: article.content,
        category: article.category,
        tags: article.tags || []
      });
      
      console.log('✅ Artigo atualizado com sucesso:', result);
      navigate(createPageUrl(`ViewKnowledge?id=${article.id}`));
    } catch (error) {
      console.error('❌ Error updating knowledge article:', error);
      
      let errorMessage = 'Erro ao atualizar artigo. Por favor, tente novamente.';
      
      if (error.code === '406') {
        errorMessage = 'Erro de permissão. Verifique se você tem autorização para editar este artigo.';
      } else if (error.message) {
        errorMessage = `Erro: ${error.message}`;
      }
      
      alert(errorMessage);
    }
    
    setIsSaving(false);
  };

  const handlePreview = () => {
    // Em uma implementação real, isso poderia salvar um rascunho e mostrar uma visualização
    // Para simplificar, vamos apenas navegar para a página de visualização
    sessionStorage.setItem('previewArticle', JSON.stringify(article));
    window.open(createPageUrl(`ViewKnowledge?id=${article.id}&preview=true`), '_blank');
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(createPageUrl('Knowledge'))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Editar Artigo</h1>
            <p className="text-muted-foreground mt-1">Base de Conhecimento</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handlePreview}
          >
            <Eye className="mr-2 h-4 w-4" />
            Visualizar
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isSaving}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Informações do Artigo</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    placeholder="Digite o título do artigo"
                    value={article.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Conteúdo</Label>
                  <ModernEditor
                    value={article.content}
                    onChange={(content) => handleInputChange('content', content)}
                    placeholder="Digite o conteúdo do artigo..."
                    className="min-h-[400px]"
                  />
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Categoria e Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Select 
                  value={article.category || ''} 
                  onValueChange={(value) => {
                    console.log('🔄 Categoria selecionada:', value);
                    handleInputChange('category', value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Input alternativo se precisar editar manualmente */}
                <div className="mt-2">
                  <Label className="text-xs text-muted-foreground">Ou digite manualmente:</Label>
                  <Input
                    placeholder="Digite a categoria"
                    value={article.category || ''}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Categoria atual: {article.category || 'Nenhuma'} | Categorias disponíveis: {categories.length}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tags">Tags</Label>
                <div className="flex gap-2">
                  <Input
                    id="tags"
                    placeholder="Adicionar nova tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  />
                  <Button type="button" onClick={handleAddTag} variant="secondary">
                    Adicionar
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-3">
                  {article.tags && article.tags.map((tag) => (
                    <div key={tag} className="bg-primary/10 text-primary px-3 py-1 rounded-full flex items-center gap-1 text-sm">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-primary/70 hover:text-primary focus:outline-none"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  
                  {(!article.tags || article.tags.length === 0) && (
                    <p className="text-sm text-muted-foreground">Nenhuma tag adicionada</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Opções Adicionais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="related">Chamados Relacionados</Label>
                <Select disabled>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione chamados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum chamado selecionado</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  A associação com chamados está temporariamente indisponível.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}